
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { TimeEntry } from '@/types/database';
import { useToast } from '@/hooks/use-toast';
import { useMigrationStore } from '@/store/migrationStore';
import { getUserPinOrThrow, shouldUsePinMode } from '@/utils/pinModeUtils';

// Types pour les mutations
interface CreateTimeEntryData {
  user_id: string;
  project_id: string;
  start_time: string;
  end_time?: string;
  duration_minutes?: number;
  description?: string;
  is_billable?: boolean;
}

interface UpdateTimeEntryData {
  id: string;
  updates: Partial<TimeEntry>;
}

// Fonctions d'API
const fetchTimeEntries = async (userId: string, isStagingMode: boolean): Promise<TimeEntry[]> => {
  // En mode PIN, on n'utilise pas les tables staging (pas de RPC équivalente) :
  // on force le chemin "PIN RPC" sur les vraies tables.
  const pinMode = shouldUsePinMode();
  const effectiveStagingMode = isStagingMode && !pinMode;
  const tableName = effectiveStagingMode ? 'time_entries_staging' : 'time_entries';
  
  if (pinMode) {
    const pin = getUserPinOrThrow();
    const { data, error } = await supabase.rpc('pin_list_user_time_entries', {
      p_pin: pin,
      p_user_id: userId,
      p_start: null,
      p_end: null,
      p_limit: 1000,
      p_offset: 0,
    });
    if (error) throw error;
    return (data || []) as unknown as TimeEntry[];
  }

  const { data, error } = await supabase.from(tableName).select('*').eq('user_id', userId).order('start_time', { ascending: false });

  if (error) throw error;
  return data || [];
};

const createTimeEntry = async (entry: CreateTimeEntryData, isStagingMode: boolean): Promise<TimeEntry> => {
  const pinMode = shouldUsePinMode();
  const effectiveStagingMode = isStagingMode && !pinMode;
  const tableName = effectiveStagingMode ? 'time_entries_staging' : 'time_entries';
  
  if (pinMode) {
    const pin = getUserPinOrThrow();

    const { data: entryId, error } = await supabase.rpc('pin_create_time_entry', {
      p_pin: pin,
      p_user_id: entry.user_id,
      p_project_id: entry.project_id,
      p_start_time: entry.start_time,
      p_description: entry.description ?? null,
      p_is_billable: entry.is_billable ?? true,
      p_status: entry.end_time ? 'closed' : 'active',
    });
    if (error) throw error;
    if (!entryId) throw new Error("Aucun ID d'entrée retourné");

    // Si entrée fermée, on applique la fin/durée via update RPC.
    if (entry.end_time) {
      const { error: updErr } = await supabase.rpc('pin_update_time_entry', {
        p_pin: pin,
        p_entry_id: entryId,
        p_end_time: entry.end_time,
        p_duration_minutes: entry.duration_minutes ?? null,
        p_description: entry.description ?? null,
        p_is_billable: entry.is_billable ?? null,
        p_status: 'closed',
      });
      if (updErr) throw updErr;
    }

    return {
      id: entryId as unknown as string,
      user_id: entry.user_id,
      project_id: entry.project_id,
      start_time: entry.start_time,
      end_time: entry.end_time ?? null,
      duration_minutes: entry.duration_minutes ?? null,
      description: entry.description ?? null,
      is_billable: entry.is_billable ?? true,
      // Les autres champs (status, hourly_rate, etc.) sont optionnels côté type DB
    } as unknown as TimeEntry;
  }

  const { data, error } = await supabase.from(tableName).insert([entry]).select().single();

  if (error) throw error;
  return data;
};

const updateTimeEntry = async ({ id, updates }: UpdateTimeEntryData, isStagingMode: boolean): Promise<TimeEntry> => {
  const pinMode = shouldUsePinMode();
  const effectiveStagingMode = isStagingMode && !pinMode;
  const tableName = effectiveStagingMode ? 'time_entries_staging' : 'time_entries';
  
  if (pinMode) {
    const pin = getUserPinOrThrow();
    const { error } = await supabase.rpc('pin_update_time_entry', {
      p_pin: pin,
      p_entry_id: id,
      p_end_time: (updates as any).end_time ?? null,
      p_duration_minutes: (updates as any).duration_minutes ?? null,
      p_description: (updates as any).description ?? null,
      p_is_billable: (updates as any).is_billable ?? null,
      p_status: (updates as any).status ?? null,
      p_project_id: (updates as any).project_id ?? null,
    });
    if (error) throw error;

    // La RPC ne retourne pas l'objet : on reconstruit à partir des updates.
    return { id, ...(updates as any) } as TimeEntry;
  }

  const { data, error } = await supabase.from(tableName).update(updates).eq('id', id).select().single();

  if (error) throw error;
  return data;
};

const deleteTimeEntry = async (id: string, isStagingMode: boolean): Promise<void> => {
  const pinMode = shouldUsePinMode();
  const effectiveStagingMode = isStagingMode && !pinMode;
  const tableName = effectiveStagingMode ? 'time_entries_staging' : 'time_entries';
  
  if (pinMode) {
    const pin = getUserPinOrThrow();
    const { error } = await supabase.rpc('pin_delete_time_entry', {
      p_pin: pin,
      p_entry_id: id,
    });
    if (error) throw error;
    return;
  }

  const { error } = await supabase.from(tableName).delete().eq('id', id);

  if (error) throw error;
};

export const useTimeEntries = (userId?: string) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { isActive, source } = useMigrationStore();
  const isStagingMode = isActive && source === 'staging';

  // Query pour récupérer les entrées de temps
  const {
    data: timeEntries = [],
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['timeEntries', userId, isStagingMode],
    queryFn: () => fetchTimeEntries(userId!, isStagingMode),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    onError: (error) => {
      console.error('Erreur lors du chargement des entrées de temps:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les entrées de temps",
        variant: "destructive",
      });
    }
  });

  // Mutation pour créer une entrée
  const createMutation = useMutation({
    mutationFn: (entry: CreateTimeEntryData) => createTimeEntry(entry, isStagingMode),
    onSuccess: (newEntry) => {
      queryClient.setQueryData(['timeEntries', userId, isStagingMode], (old: TimeEntry[] = []) => [newEntry, ...old]);
      toast({
        title: "Succès",
        description: "Entrée de temps créée avec succès",
      });
    },
    onError: (error) => {
      console.error('Erreur lors de la création de l\'entrée:', error);
      toast({
        title: "Erreur",
        description: "Impossible de créer l'entrée de temps",
        variant: "destructive",
      });
    }
  });

  // Mutation pour mettre à jour une entrée
  const updateMutation = useMutation({
    mutationFn: ({ id, updates }: UpdateTimeEntryData) => updateTimeEntry({ id, updates }, isStagingMode),
    onSuccess: (updatedEntry) => {
      queryClient.setQueryData(['timeEntries', userId, isStagingMode], (old: TimeEntry[] = []) => 
        old.map(entry => entry.id === updatedEntry.id ? updatedEntry : entry)
      );
      toast({
        title: "Succès",
        description: "Entrée de temps mise à jour",
      });
    },
    onError: (error) => {
      console.error('Erreur lors de la mise à jour:', error);
      toast({
        title: "Erreur",
        description: "Impossible de mettre à jour l'entrée",
        variant: "destructive",
      });
    }
  });

  // Mutation pour supprimer une entrée
  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteTimeEntry(id, isStagingMode),
    onSuccess: (_, deletedId) => {
      queryClient.setQueryData(['timeEntries', userId, isStagingMode], (old: TimeEntry[] = []) => 
        old.filter(entry => entry.id !== deletedId)
      );
      toast({
        title: "Succès",
        description: "Entrée de temps supprimée",
      });
    },
    onError: (error) => {
      console.error('Erreur lors de la suppression:', error);
      toast({
        title: "Erreur",
        description: "Impossible de supprimer l'entrée",
        variant: "destructive",
      });
    }
  });

  // Fonctions utilitaires
  const createTimeEntryFn = (entry: CreateTimeEntryData) => {
    return createMutation.mutateAsync(entry);
  };

  const updateTimeEntryFn = (id: string, updates: Partial<TimeEntry>) => {
    return updateMutation.mutateAsync({ id, updates });
  };

  const deleteTimeEntryFn = (id: string) => {
    return deleteMutation.mutateAsync(id);
  };

  // Statistiques calculées
  const stats = {
    totalEntries: timeEntries.length,
    totalHours: timeEntries.reduce((sum, entry) => sum + (entry.duration_minutes || 0), 0) / 60,
    todayHours: timeEntries
      .filter(entry => {
        const today = new Date().toDateString();
        return new Date(entry.start_time).toDateString() === today;
      })
      .reduce((sum, entry) => sum + (entry.duration_minutes || 0), 0) / 60,
    thisWeekHours: timeEntries
      .filter(entry => {
        const startOfWeek = new Date();
        startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay() + 1);
        return new Date(entry.start_time) >= startOfWeek;
      })
      .reduce((sum, entry) => sum + (entry.duration_minutes || 0), 0) / 60
  };

  return {
    // Données
    timeEntries,
    stats,
    
    // États
    isLoading: isLoading || createMutation.isPending || updateMutation.isPending || deleteMutation.isPending,
    error,
    
    // Actions
    createTimeEntry: createTimeEntryFn,
    updateTimeEntry: updateTimeEntryFn,
    deleteTimeEntry: deleteTimeEntryFn,
    refetch,
    
    // États des mutations
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending
  };
};
